import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import {
  Search, SlidersHorizontal, Star, MapPin, Shield, Truck,
  BadgeCheck, X, Bike, ChevronDown, Heart, Calendar, Edit3
} from 'lucide-react';
import { Header } from '@/components/Header';
import { CollapsibleText } from '@/components/CollapsibleText';
import { SEO, breadcrumbJsonLd } from '@/components/SEO';
import { numberLocale } from '@/lib/format';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { RidaloCheckbox } from '@/components/ui/ridalo-checkbox';
import { DualRangeSlider } from '@/components/ui/dual-range-slider';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { BRANDS, CATEGORIES } from '@/data/scooters';
import { DESTINATIONS, DESTINATION_LIST, getZonesForDestination, inferDestinationFromLocation, normalizeDestinationId, normalizeZone as normalizeZoneForDest } from '@/data/destinations';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { AvailabilitySearchBar } from '@/components/AvailabilitySearchBar';
import { formatDateOnly } from '@/lib/dateFR';
import { pickTranslation } from '@/i18n/pickTranslation';
import { currentUrlLang, pathInLang } from '@/i18n/urlLanguage';
import { publicProviderName } from '@/lib/providerName';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRICE_MIN = 150;
const PRICE_MAX = 2000;

const FALLBACK_IMG = 'https://customer-assets.emergentagent.com/job_journey-link-13/artifacts/j9icv8bt_Image.png';

// -------------------------------------------------------------------------
// Zone normalization — matches the canonical zone casing from destinations.js
// (e.g. "rawai" → "Rawai") with a Title Case fallback for legacy values.// -------------------------------------------------------------------------
const normalizeZone = (raw, destinationId = null) => {
  if (!raw) return '';
  const cleaned = String(raw).trim().replace(/\s+/g, ' ');
  const lower = cleaned.toLowerCase();
  // Look up canonical zone name across all destinations (so legacy locations
  // without a destination still get matched to the right casing).
  const checkDest = (id) => {
    const zones = getZonesForDestination(id);
    return zones.find((z) => z.toLowerCase() === lower);
  };
  if (destinationId) {
    const found = checkDest(destinationId);
    if (found) return found;
  }
  for (const d of DESTINATION_LIST) {
    const found = checkDest(d.id);
    if (found) return found;
  }
  return cleaned.replace(/\b\w/g, (c) => c.toUpperCase());
};

// Map a backend "service" document (provider-created listing) into the
// shape expected by the marketplace card renderer below. Uses lenient
// fallbacks so listings still show even if the provider omitted fields.
// Phase 2.4: pickTranslation() prefers title_translations[lang] /
// description_translations[lang] when available, else falls back to the
// original fields.
const dbServiceToScooter = (svc, lang = 'fr') => {
  const d = svc.details || {};
  const destId = svc.destination || inferDestinationFromLocation(svc.location || svc.zone || '');
  const zone = svc.zone || normalizeZone(svc.location, destId);
  return {
    id: svc.service_id,
    name: pickTranslation(svc, 'title', lang) || svc.title,
    brand: d.brand || 'Autre',
    category: d.category || 'urbain',
    price: svc.price || 0,
    destinationId: destId,
    destinationName: (DESTINATIONS[destId]?.name) || 'Phuket',
    zone: zone || 'Phuket',
    rating: 0,
    reviews: 0,
    year: d.year || new Date().getFullYear(),
    color: d.color || '',
    engine: d.engine || 125,
    minRentalDuration: Math.max(1, Math.min(30, parseInt(d.minimum_rental_duration) || 1)),
    helmets: d.helmets ?? 2,
    insurance: d.insurance ?? false,
    delivery: !!(svc.delivery_available ?? d.delivery_available),
    deliveryZones: svc.delivery_zones || [],
    freeDelivery: false,
    deposit: d.deposit ?? 0,
    passport: d.passport_required ?? false,
    fuelPolicy: d.fuel_policy || 'Rendu plein',
    provider: { name: publicProviderName(svc, lang), verified: !!svc.provider_verified, joinedYear: new Date().getFullYear() },
    priceTiers: svc.price_tiers || [],
    description: pickTranslation(svc, 'description', lang) || svc.description || '',
    images: (svc.images && svc.images.length ? svc.images : [FALLBACK_IMG]),
  };
};

const ScooterCard = ({ scooter, isFavorite, onToggleFavorite, periodQuery = '' }) => {
  const { t } = useTranslation();
  return (
  <Link
    to={`/scooters/${scooter.id}${periodQuery}`}
    className="group block card-offer overflow-hidden"
    data-testid={`scooter-card-${scooter.id}`}
  >
    <div className="relative h-56 overflow-hidden bg-slate-100">
      <img
        src={scooter.images[0]}
        alt={scooter.name}
        width="640" height="416"
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        loading="lazy"
      />
      {scooter.provider.verified && (
        <div className="absolute top-3 left-3 inline-flex items-center gap-1 bg-white/95 backdrop-blur-sm rounded-full px-3 py-1 text-xs font-semibold text-primary shadow-sm">
          <BadgeCheck className="w-3.5 h-3.5 text-accent" />
          {t('scooters.filters.verifiedProvider')}
        </div>
      )}
      {scooter.freeDelivery && (
        <div className="absolute top-3 right-3 inline-flex items-center gap-1 bg-accent text-white rounded-full px-3 py-1 text-xs font-semibold shadow-sm">
          <Truck className="w-3.5 h-3.5" />
          {t('scooters.card.freeDeliveryBadge')}
        </div>
      )}
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleFavorite?.(scooter); }}
        className={`absolute bottom-3 right-3 w-9 h-9 rounded-full flex items-center justify-center backdrop-blur-sm shadow-sm transition-all ${isFavorite ? 'bg-red-500 text-white' : 'bg-white/90 text-primary hover:bg-white'}`}
        aria-label={isFavorite ? t('scooters.card.favoriteRemove') : t('scooters.card.favoriteAdd')}
        data-testid={`fav-toggle-${scooter.id}`}
      >
        <Heart className={`w-4 h-4 ${isFavorite ? 'fill-current' : ''}`} />
      </button>
    </div>

    <div className="p-5">
      <div className="flex items-start justify-between gap-2 mb-1">
        <h3 className="text-lg font-semibold text-primary leading-tight" data-testid={`scooter-name-${scooter.id}`}>
          {scooter.name}
        </h3>
        {/* Only with real reviews — never a « ★ 0 (0) » placeholder. */}
        {scooter.reviews > 0 && scooter.rating > 0 && (
          <div className="flex items-center gap-1 shrink-0" data-testid={`scooter-rating-${scooter.id}`}>
            <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
            <span className="text-sm font-semibold text-primary">{scooter.rating}</span>
            <span className="text-xs text-ink-muted">({scooter.reviews})</span>
          </div>
        )}
      </div>

      <div className="flex items-center text-sm text-ink-light mb-3">
        <MapPin className="w-4 h-4 mr-1 text-accent" />
        {scooter.zone}
      </div>

      <div className="flex flex-wrap gap-1.5 mb-4">
        <span className="inline-flex items-center gap-1 bg-background-soft text-primary text-xs font-medium rounded-full px-2.5 py-1">
          🪖 {t('scooters.card.helmets', { count: scooter.helmets })}
        </span>
        {scooter.freeDelivery ? (
          <span className="inline-flex items-center gap-1 bg-accent/10 text-accent text-xs font-medium rounded-full px-2.5 py-1">
            🚚 {t('scooters.card.deliveryIncluded')}
          </span>
        ) : scooter.delivery ? (
          <span className="inline-flex items-center gap-1 bg-background-soft text-primary text-xs font-medium rounded-full px-2.5 py-1">
            🚚 {t('scooters.card.deliveryBadge')}
          </span>
        ) : null}
      </div>

      <div className="flex items-end justify-between pt-3 border-t border-slate-100">
        <div className="flex items-baseline gap-1">
          <span className="text-2xl font-bold text-primary">{scooter.price}</span>
          <span className="text-sm text-ink-light">{t('scooters.card.pricePerDay')}</span>
        </div>
        <span className="text-sm font-semibold text-accent group-hover:text-accent-hover transition-colors">
          {t('scooters.card.viewOffer')}
        </span>
      </div>
    </div>
  </Link>
  );
};

const FilterPanel = ({ filters, setFilters, onReset, count, zones }) => {
  const { t } = useTranslation();
  const toggleArray = (key, value) => {
    setFilters((prev) => {
      const list = prev[key];
      return { ...prev, [key]: list.includes(value) ? list.filter((v) => v !== value) : [...list, value] };
    });
  };

  // Phase 2.3+: localize the aggregate zone labels defined in data/destinations.js
  // ("Toute l'île de Phuket", "Toute la zone de Krabi", "Toute la ville de Bangkok").
  // We transform ONLY the displayed text; the underlying zone string stays identical
  // so filter comparisons (filters.zones.includes(z) and svc.zone matching) are untouched.
  const displayZone = (z) => {
    if (typeof z !== 'string') return z;
    let m = z.match(/^Toute l'île de (.+)$/);
    if (m) return t('scooters.zoneLabels.wholeIslandOf', { name: m[1] });
    m = z.match(/^Toute la zone de (.+)$/);
    if (m) return t('scooters.zoneLabels.wholeAreaOf', { name: m[1] });
    m = z.match(/^Toute la ville de (.+)$/);
    if (m) return t('scooters.zoneLabels.wholeCityOf', { name: m[1] });
    return z;
  };

  return (
    <div className="space-y-6" data-testid="filter-panel">
      {/* Zone */}
      <div>
        <h4 className="text-sm font-semibold text-primary mb-3 flex items-center gap-2">
          <MapPin className="w-4 h-4 text-accent" /> {t('scooters.filters.zone')}
        </h4>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setFilters((p) => ({ ...p, zones: [] }))}
            className={`text-xs font-medium rounded-full px-3 py-1.5 border transition-all ${filters.zones.length === 0 ? 'bg-accent text-white border-accent' : 'bg-white text-primary border-slate-200 hover:border-accent'}`}
            data-testid="filter-zone-all"
          >
            {t('scooters.filters.allZones')}
          </button>
          {zones.map((z) => (
            <button
              key={z}
              type="button"
              onClick={() => toggleArray('zones', z)}
              className={`text-xs font-medium rounded-full px-3 py-1.5 border transition-all ${filters.zones.includes(z) ? 'bg-accent text-white border-accent' : 'bg-white text-primary border-slate-200 hover:border-accent'}`}
              data-testid={`filter-zone-${z.toLowerCase().replace(/\s/g, '-')}`}
            >
              {displayZone(z)}
            </button>
          ))}
        </div>
      </div>

      {/* Prix */}
      <div>
        <h4 className="text-sm font-semibold text-primary mb-3">{t('scooters.filters.pricePerDay')}</h4>
        <div className="px-1">
          <DualRangeSlider
            min={PRICE_MIN}
            max={PRICE_MAX}
            step={50}
            value={filters.price}
            onValueChange={(v) => setFilters((p) => ({ ...p, price: v }))}
            data-testid="filter-price-slider"
          />
          <div className="flex justify-between text-xs text-ink-light mt-2">
            <span>{filters.price[0]} THB</span>
            <span>{filters.price[1] === PRICE_MAX ? '2000+ THB' : `${filters.price[1]} THB`}</span>
          </div>
        </div>
      </div>

      {/* Marque */}
      <div>
        <h4 className="text-sm font-semibold text-primary mb-3">{t('scooters.filters.brand')}</h4>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => toggleArray('brands', 'Honda')}
            className={`text-sm font-medium rounded-xl px-3 py-2.5 border transition-all ${filters.brands.includes('Honda') ? 'bg-accent text-white border-accent' : 'bg-white text-primary border-slate-200 hover:border-accent'}`}
            data-testid="filter-brand-honda"
          >
            Honda
          </button>
          <button
            type="button"
            onClick={() => toggleArray('brands', 'Yamaha')}
            className={`text-sm font-medium rounded-xl px-3 py-2.5 border transition-all ${filters.brands.includes('Yamaha') ? 'bg-accent text-white border-accent' : 'bg-white text-primary border-slate-200 hover:border-accent'}`}
            data-testid="filter-brand-yamaha"
          >
            Yamaha
          </button>
          <button
            type="button"
            onClick={() => toggleArray('brands', 'Autres')}
            className={`text-sm font-medium rounded-xl px-3 py-2.5 border transition-all ${filters.brands.includes('Autres') ? 'bg-accent text-white border-accent' : 'bg-white text-primary border-slate-200 hover:border-accent'}`}
            data-testid="filter-brand-autres"
          >
            {t('scooters.filters.brandOthers')}
          </button>
        </div>
      </div>

      {/* Catégorie */}
      <div>
        <h4 className="text-sm font-semibold text-primary mb-3">{t('scooters.filters.category')}</h4>
        <div className="grid grid-cols-2 gap-2">
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => toggleArray('categories', c.id)}
              className={`text-sm font-medium rounded-xl px-3 py-2.5 border transition-all text-left ${filters.categories.includes(c.id) ? 'bg-accent/10 text-accent border-accent' : 'bg-white text-primary border-slate-200 hover:border-accent'}`}
              data-testid={`filter-category-${c.id}`}
            >
              {t(`scooters.categories.${c.id}`, { defaultValue: c.label })}
            </button>
          ))}
        </div>
      </div>

      {/* Livraison + Verified */}
      <div>
        <h4 className="text-sm font-semibold text-primary mb-3">{t('scooters.filters.options')}</h4>
        <div className="space-y-3">
          <label className="flex items-center gap-3 cursor-pointer">
            <RidaloCheckbox
              checked={filters.delivery}
              onCheckedChange={(v) => setFilters((p) => ({ ...p, delivery: !!v }))}
              data-testid="filter-delivery"
            />
            <span className="text-sm text-primary">{t('scooters.filters.deliveryAvailable')}</span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <RidaloCheckbox
              checked={filters.freeDelivery}
              onCheckedChange={(v) => setFilters((p) => ({ ...p, freeDelivery: !!v }))}
              data-testid="filter-free-delivery"
            />
            <span className="text-sm text-primary">{t('scooters.filters.deliveryFree')}</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <RidaloCheckbox
              checked={filters.verifiedOnly}
              onCheckedChange={(v) => setFilters((p) => ({ ...p, verifiedOnly: !!v }))}
              data-testid="filter-verified"
            />
            <span className="text-sm text-primary flex items-center gap-1 whitespace-nowrap">
              <BadgeCheck className="w-4 h-4 text-accent shrink-0" /> {t('scooters.filters.verifiedProvider')}
            </span>
          </label>
        </div>
      </div>

      <div className="pt-2 flex items-center justify-between gap-3 border-t border-slate-100">
        <button
          type="button"
          onClick={onReset}
          className="text-sm font-medium text-ink-light hover:text-primary transition-colors"
          data-testid="filter-reset"
        >
          {t('scooters.filters.reset')}
        </button>
        <span className="text-sm text-primary font-semibold" data-testid="filter-count">
          {t('scooters.filters.resultsCount', { count })}
        </span>
      </div>
    </div>
  );
};

export const ScooterMarketplace = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  // Destination: prefer explicit URL param, else infer from legacy `location` param.
  const legacyLoc = searchParams.get('location') || searchParams.get('zone') || '';
  const urlDestinationRaw = searchParams.get('destination') || '';
  const initialDestinationId = normalizeDestinationId(urlDestinationRaw)
    || (legacyLoc ? inferDestinationFromLocation(legacyLoc) : 'phuket');
  const initialZone = normalizeZone(legacyLoc, initialDestinationId);
  const urlStartDate = searchParams.get('start_date') || '';
  const urlEndDate = searchParams.get('end_date') || '';
  const urlStartTime = searchParams.get('start_time') || '';
  const urlEndTime = searchParams.get('end_time') || '';
  const hasSearch = !!(urlStartDate && urlEndDate);
  const browseMode = searchParams.get('browse') !== '0' && !hasSearch; // Default to browse unless explicitly disabled or search active
  const hasEntered = true; // Always show catalogue directly

  const { user } = useAuth();
  const [favIds, setFavIds] = useState(new Set());
  const [dbListings, setDbListings] = useState([]);
  const [loadingListings, setLoadingListings] = useState(false);
  // Currently selected destination (drives the available zones list).
  const [destinationId, setDestinationId] = useState(initialDestinationId);
  const destinationName = DESTINATIONS[destinationId]?.name || 'Phuket';
  // Zones for the chosen destination (canonical list + anything the backend
  // returns that doesn't yet appear in the canonical list).
  const canonicalZones = useMemo(() => getZonesForDestination(destinationId), [destinationId]);

  // Fetch listings for the selected destination. Browse mode = all approved
  // listings; search mode = adds availability window filter.
  useEffect(() => {
    if (!hasEntered) { setDbListings([]); return; }
    const params = new URLSearchParams({ category: 'scooter' });
    if (hasSearch) {
      params.set('start_date', urlStartDate);
      params.set('end_date', urlEndDate);
      params.set('start_time', urlStartTime || '12:00');
      params.set('end_time', urlEndTime || '12:00');
    }
    if (destinationId) params.set('destination', destinationId);
    setLoadingListings(true);
    axios.get(`${API}/services?${params.toString()}`)
      .then((r) => setDbListings((r.data || []).map((s) => dbServiceToScooter(s, i18n.language))))
      .catch(() => setDbListings([]))
      .finally(() => setLoadingListings(false));
  }, [hasEntered, hasSearch, urlStartDate, urlEndDate, urlStartTime, urlEndTime, destinationId, i18n.language]);

  const allScooters = useMemo(() => {
    // All scooters come from MongoDB — no static demo fallback. Older demo
    // entries (sc-01…sc-14) were injected as a placeholder before V1; they are
    // now fully retired to keep Admin ↔ traveler views perfectly consistent.
    return [...dbListings];
  }, [dbListings]);

  // Lowest daily price among the listings actually loaded (display only).
  const phuketMinPrice = useMemo(() => {
    if (destinationId !== 'phuket') return 0;
    const prices = allScooters.map((s) => Number(s.price) || 0).filter((p) => p > 0);
    return prices.length ? Math.min(...prices) : 0;
  }, [allScooters, destinationId]);

  useEffect(() => {
    if (user?.role !== 'client') return;
    axios.get(`${API}/favorites`, { withCredentials: true })
      .then((r) => setFavIds(new Set((r.data || []).map((f) => f.service_id))))
      .catch(() => {});
  }, [user]);

  const toggleFavorite = async (scooter) => {
    if (!user) { toast.error(t('scooters.toasts.loginToFav')); return; }
    if (user.role !== 'client') { toast.error(t('scooters.toasts.travelerOnly')); return; }
    const isFav = favIds.has(scooter.id);
    try {
      if (isFav) {
        await axios.delete(`${API}/favorites/${scooter.id}`, { withCredentials: true });
        setFavIds((prev) => { const s = new Set(prev); s.delete(scooter.id); return s; });
        toast.success(t('scooters.toasts.removedFromFav'));
      } else {
        await axios.post(`${API}/favorites`, {
          service_id: scooter.id,
          snapshot: { title: scooter.name, image: scooter.images?.[0], price: scooter.price, location: scooter.zone, category: 'scooter' },
        }, { withCredentials: true });
        setFavIds((prev) => new Set(prev).add(scooter.id));
        toast.success(t('scooters.toasts.addedToFav'));
      }
    } catch { toast.error(t('scooters.toasts.error')); }
  };

  const [filters, setFilters] = useState({
    query: '',
    zones: initialZone ? [initialZone] : [],
    brands: [],
    categories: [],
    price: [PRICE_MIN, PRICE_MAX],
    delivery: false,
    freeDelivery: false,
    verifiedOnly: false,
  });

  const [sortBy, setSortBy] = useState('recommended');
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  // Mobile: the date search lives in a « Dates » sheet instead of the big form.
  const [mobileDatesOpen, setMobileDatesOpen] = useState(false);

  // Zones = union of the canonical zones for the chosen destination and the
  // actual normalized zones coming from active listings.
  const zones = useMemo(() => {
    const set = new Set(canonicalZones);
    for (const s of allScooters) if (s.zone) set.add(s.zone);
    // Remove the generic destination fallback (e.g. "Phuket") since it should
    // not appear as a filter chip when the "Toute l'île" button exists.
    set.delete(destinationName);
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'fr'));
  }, [allScooters, canonicalZones, destinationName]);

  const resetFilters = () => setFilters({
    query: '',
    zones: [],
    brands: [],
    categories: [],
    price: [PRICE_MIN, PRICE_MAX],
    delivery: false,
    freeDelivery: false,
    verifiedOnly: false,
  });

  const filtered = useMemo(() => {
    let list = allScooters.filter((s) => {
      if (filters.query && !s.name.toLowerCase().includes(filters.query.toLowerCase())) return false;
      if (filters.zones.length && !filters.zones.includes(s.zone)) return false;

      // Brand filter: "Autres" dynamically includes all brands except Honda and Yamaha
      if (filters.brands.length) {
        const matchesHonda = filters.brands.includes('Honda') && s.brand === 'Honda';
        const matchesYamaha = filters.brands.includes('Yamaha') && s.brand === 'Yamaha';
        const matchesAutres = filters.brands.includes('Autres') && s.brand !== 'Honda' && s.brand !== 'Yamaha';
        if (!matchesHonda && !matchesYamaha && !matchesAutres) return false;
      }

      if (filters.categories.length && !filters.categories.includes(s.category)) return false;
      if (s.price < filters.price[0] || (filters.price[1] < PRICE_MAX && s.price > filters.price[1])) return false;
      if (filters.delivery && !s.delivery) return false;
      if (filters.freeDelivery && !s.freeDelivery) return false;
      if (filters.verifiedOnly && !s.provider.verified) return false;
      return true;
    });

    switch (sortBy) {
      case 'price-asc': list = [...list].sort((a, b) => a.price - b.price); break;
      case 'price-desc': list = [...list].sort((a, b) => b.price - a.price); break;
      case 'rating': list = [...list].sort((a, b) => b.rating - a.rating); break;
      default: break;
    }
    return list;
  }, [allScooters, filters, sortBy]);

  const activeFilterCount =
    filters.zones.length +
    filters.brands.length +
    filters.categories.length +
    (filters.delivery ? 1 : 0) +
    (filters.freeDelivery ? 1 : 0) +
    (filters.verifiedOnly ? 1 : 0) +
    (filters.price[0] > PRICE_MIN || filters.price[1] < PRICE_MAX ? 1 : 0);

  const applySearch = ({ location, startDate, endDate, startTime, endTime }) => {
    const next = new URLSearchParams();
    next.set('destination', destinationId);
    if (location) next.set('location', location);
    if (startDate) next.set('start_date', startDate);
    if (endDate) next.set('end_date', endDate);
    if (startTime) next.set('start_time', startTime);
    if (endTime) next.set('end_time', endTime);
    setSearchParams(next);
    // Sync the zone filter with the search bar location (single source of truth).
    setFilters((p) => ({ ...p, zones: location ? [location] : [] }));
  };

  const browseAll = () => {
    const next = new URLSearchParams();
    next.set('browse', '1');
    next.set('destination', destinationId);
    setSearchParams(next);
    setFilters((p) => ({ ...p, zones: [] }));
  };

  const changeDestination = (newId) => {
    if (!newId || newId === destinationId) return;
    setDestinationId(newId);
    // Reset URL + filters when switching destinations.
    const next = new URLSearchParams();
    next.set('destination', newId);
    next.set('browse', '1');
    setSearchParams(next);
    setFilters((p) => ({ ...p, zones: [] }));
  };

  const [editSearchOpen, setEditSearchOpen] = useState(false);

  // The searched period follows the traveller to the scooter page (booking form pre-filled).
  const periodQuery = hasSearch
    ? `?${new URLSearchParams({ start_date: urlStartDate, end_date: urlEndDate, start_time: urlStartTime || '12:00', end_time: urlEndTime || '12:00' }).toString()}`
    : '';

  return (
    <div className="min-h-screen bg-background" data-testid="scooter-marketplace">
      <SEO
        title={t('seo.scooters.title')}
        description={t('seo.scooters.description')}
        canonical="https://ridalo.co/scooters"
        jsonLd={breadcrumbJsonLd([
          { name: t('seo.breadcrumb.home'), path: '/' },
          { name: t('seo.breadcrumb.scooters'), path: '/scooters' },
        ])}
      />
      <Header />

      <div className="pt-20 lg:pt-24 pb-12">
        {/* Intro Section — compact: short H1; from lg, ONE line of the descriptive text + « Read more » (full text in a
            dialog, and in the page's HTML) and the Patong link on the same row. Below lg the page is unchanged. */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-1 lg:pt-4">
          <div className="text-center mb-2 lg:mb-3">
            <h1 className="text-2xl sm:text-4xl font-bold tracking-tight text-primary lg:mb-1">
              {t('scooters.mobileTitle')}
            </h1>
            <div className="flex flex-col items-center lg:flex-row lg:justify-center lg:gap-4" data-testid="scooters-intro-row">
              <div className="hidden lg:flex min-w-0 flex-1" data-testid="scooters-intro-wrap">
                <CollapsibleText always title={t('scooters.mobileTitle')} className="text-base text-ink-light text-left" testId="scooters-intro">
                  <span>{t('scooters.introTail')}. </span>
                  <Trans i18nKey="scooters.introText" components={{ strong: <strong /> }} />
                  {phuketMinPrice > 0 && <>{' '}{t('scooters.introFromPrice', { price: phuketMinPrice.toLocaleString(numberLocale()) })}</>}
                </CollapsibleText>
              </div>
              <Link
                to={pathInLang(currentUrlLang() || 'fr', '/location-scooter-patong')}
                className="mt-0 lg:mt-0 shrink-0 inline-flex items-center gap-1 text-sm font-medium text-accent hover:underline max-sm:min-h-10"
                data-testid="scooters-local-patong-link"
              >
                <MapPin className="w-4 h-4 shrink-0" aria-hidden="true" />
                {t('scooters.localPatongLink')}
              </Link>
            </div>
          </div>
        </div>

        {/* Search summary + edit */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-3 hidden lg:block">
            <div className="flex items-center gap-2 text-xs text-ink-light mb-1">
              <Link to="/" className="hover:text-accent">{t('scooters.breadcrumbHome')}</Link>
              <span>/</span>
              <span className="text-primary font-medium">{t('scooters.breadcrumbRentals')}</span>
            </div>
            <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
              <h2 className="text-xl lg:text-2xl font-semibold tracking-tight text-primary">
                {initialZone
                  ? t('scooters.heroTitleAtZone', { zone: initialZone })
                  : (browseMode
                      ? t('scooters.heroTitleAllAt', { destination: destinationName })
                      : t('scooters.heroTitleAvailableAt', { destination: destinationName }))}
              </h2>
              <p className="text-sm text-ink-light" data-testid="results-count-row">
                <span data-testid="results-count">
                  {loadingListings
                    ? t('scooters.loading')
                    : browseMode
                      ? t('scooters.countBrowseMode', { count: filtered.length })
                      : t('scooters.countAvailable', { count: filtered.length })}
                </span>
                {/* Availability status: kept, discreetly, next to the results */}
                {browseMode ? (
                  <span className="inline-flex items-center gap-1.5 ml-3 text-xs text-ink-muted" data-testid="availability-status">
                    <Bike className="w-3.5 h-3.5 text-accent shrink-0" />{t('scooters.consultationMode')}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 ml-3 text-xs text-primary" data-testid="availability-status">
                    <Calendar className="w-3.5 h-3.5 text-accent shrink-0" />
                    <span>
                      <strong>{initialZone || t('scooters.wholeZoneOf', { destination: destinationName })}</strong> · {t('scooters.timeRangeFrom')} <strong>{formatDateOnly(urlStartDate)} {urlStartTime}</strong> {t('scooters.timeRangeTo')}{' '}
                      <strong>{formatDateOnly(urlEndDate)} {urlEndTime}</strong>
                    </span>
                  </span>
                )}
              </p>
            </div>
          </div>

          {/* Destination picker */}
          <div className="mb-3 hidden lg:flex flex-wrap gap-2" data-testid="destination-picker">
            <span className="text-xs font-semibold text-ink-light self-center mr-1">{t('scooters.destinationLabel')}</span>
            {DESTINATION_LIST.map((d) => (
              <button
                key={d.id}
                type="button"
                onClick={() => changeDestination(d.id)}
                className={`text-xs font-medium rounded-full px-3 py-1.5 border transition-all ${destinationId === d.id ? 'bg-accent text-white border-accent' : 'bg-white text-primary border-slate-200 hover:border-accent'}`}
                data-testid={`destination-${d.id}`}
              >
                {d.name}
              </button>
            ))}
          </div>

          {/* Availability Search Bar - desktop (mobile: « Dates » sheet) */}
          <div className="mb-4 hidden lg:block">
            <AvailabilitySearchBar
              variant="bar"
              zones={zones}
              defaults={{
                location: initialZone,
                startDate: urlStartDate,
                endDate: urlEndDate,
                startTime: urlStartTime,
                endTime: urlEndTime,
              }}
              onSubmit={applySearch}
              onBrowseAll={browseAll}
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-3 mb-3 lg:mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
              <Input
                type="text"
                placeholder={t('scooters.searchPlaceholder')}
                value={filters.query}
                onChange={(e) => setFilters((p) => ({ ...p, query: e.target.value }))}
                className="input-field pl-12"
                data-testid="search-scooter-input"
              />
            </div>

            <div className="grid grid-cols-[auto_auto_1fr] gap-2 sm:flex sm:gap-3" data-testid="mobile-search-actions">
              {/* Mobile « Dates » : same search form as desktop, in a sheet */}
              <div className="lg:hidden">
                <Sheet open={mobileDatesOpen} onOpenChange={setMobileDatesOpen}>
                  <SheetTrigger asChild>
                    <button className={`w-full inline-flex items-center justify-center gap-1.5 bg-white border-2 rounded-xl px-2 sm:px-4 py-3 font-semibold text-sm text-primary hover:border-accent transition-all ${hasSearch ? 'border-accent' : 'border-slate-200'}`} data-testid="open-mobile-dates">
                      <Calendar className="w-4 h-4 shrink-0" />
                      {t('scooters.datesButton')}
                      {hasSearch && <span className="w-2 h-2 rounded-full bg-accent shrink-0" />}
                    </button>
                  </SheetTrigger>
                  <SheetContent side="bottom" className="max-h-[90vh] overflow-y-auto rounded-t-3xl" data-testid="mobile-dates-sheet">
                    <SheetHeader>
                      <SheetTitle>{t('scooters.datesTitle')}</SheetTitle>
                    </SheetHeader>
                    <div className="mt-4 flex flex-wrap gap-2" data-testid="destination-picker-mobile">
                      {DESTINATION_LIST.map((d) => (
                        <button
                          key={d.id}
                          type="button"
                          onClick={() => changeDestination(d.id)}
                          className={`text-xs font-medium rounded-full px-3 py-1.5 border transition-all ${destinationId === d.id ? 'bg-accent text-white border-accent' : 'bg-white text-primary border-slate-200'}`}
                          data-testid={`destination-mobile-${d.id}`}
                        >
                          {d.name}
                        </button>
                      ))}
                    </div>
                    <div className="mt-4">
                      <AvailabilitySearchBar
                        key={`${destinationId}-${urlStartDate}-${urlEndDate}-${urlStartTime}-${urlEndTime}`}
                        zones={zones}
                        defaults={{
                          location: initialZone,
                          startDate: urlStartDate,
                          endDate: urlEndDate,
                          startTime: urlStartTime,
                          endTime: urlEndTime,
                        }}
                        onSubmit={(v) => { applySearch(v); setMobileDatesOpen(false); }}
                        onBrowseAll={() => { browseAll(); setMobileDatesOpen(false); }}
                      />
                    </div>
                    {hasSearch && (
                      <button
                        type="button"
                        onClick={() => { applySearch({ location: initialZone }); setMobileDatesOpen(false); }}
                        className="mt-4 w-full text-sm font-semibold text-accent underline"
                        data-testid="mobile-dates-clear"
                      >
                        {t('scooters.clearDates')}
                      </button>
                    )}
                  </SheetContent>
                </Sheet>
              </div>

              {/* Mobile filter trigger */}
              <div className="lg:hidden">
                <Sheet open={mobileFilterOpen} onOpenChange={setMobileFilterOpen}>
                  <SheetTrigger asChild>
                    <button className="relative w-full inline-flex items-center justify-center gap-1.5 sm:gap-2 bg-white border-2 border-slate-200 rounded-xl px-2 sm:px-4 py-3 font-semibold text-sm sm:text-base text-primary hover:border-accent transition-all" data-testid="open-mobile-filters">
                      <SlidersHorizontal className="w-4 h-4 shrink-0" />
                      {t('scooters.filtersTitle')}
                      {activeFilterCount > 0 && (
                        <span className="absolute -top-2 -right-2 sm:static bg-accent text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                          {activeFilterCount}
                        </span>
                      )}
                    </button>
                  </SheetTrigger>
                  <SheetContent side="bottom" className="h-[90vh] overflow-y-auto rounded-t-3xl">
                    <SheetHeader>
                      <SheetTitle>{t('scooters.filtersTitle')}</SheetTitle>
                    </SheetHeader>
                    <div className="mt-6">
                      <FilterPanel filters={filters} setFilters={setFilters} onReset={resetFilters} count={filtered.length} zones={zones} />
                    </div>
                    <button
                      onClick={() => setMobileFilterOpen(false)}
                      className="btn-primary w-full mt-6"
                      data-testid="apply-filters"
                    >
                      {t('scooters.showScooters', { count: filtered.length })}
                    </button>
                  </SheetContent>
                </Sheet>
              </div>

              {/* Sort */}
              <div className="relative min-w-0">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full sm:w-auto appearance-none bg-white border-2 border-slate-200 rounded-xl pl-2.5 pr-6 sm:pl-4 sm:pr-10 py-3 font-medium text-primary hover:border-accent transition-all cursor-pointer text-[13px] sm:text-sm"
                  data-testid="sort-select"
                >
                  <option value="recommended">{t('scooters.sort.recommended')}</option>
                  <option value="price-asc">{t('scooters.sort.priceAsc')}</option>
                  <option value="price-desc">{t('scooters.sort.priceDesc')}</option>
                  <option value="rating">{t('scooters.sort.rating')}</option>
                </select>
                <ChevronDown className="absolute right-1.5 sm:right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              </div>
            </div>
          </div>

          {/* Mobile: active period (discreet) + result count */}
          <p className="lg:hidden mb-4 text-xs text-ink-light" data-testid="mobile-period-summary">
            {hasSearch ? (
              <button type="button" onClick={() => setMobileDatesOpen(true)} className="text-primary font-medium" data-testid="mobile-active-period">
                📅 {formatDateOnly(urlStartDate)} {urlStartTime || '12:00'} → {formatDateOnly(urlEndDate)} {urlEndTime || '12:00'}
              </button>
            ) : null}
            {hasSearch ? ' · ' : ''}
            {loadingListings
              ? t('scooters.loading')
              : browseMode
                ? t('scooters.countBrowseMode', { count: filtered.length })
                : t('scooters.countAvailable', { count: filtered.length })}
          </p>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Sidebar filters (desktop) */}
            <aside className="hidden lg:block lg:col-span-1">
              <div className="sticky top-24 max-h-[calc(100vh-7rem)] overflow-hidden card-dashboard">
                <div className="p-5 pb-0 flex items-center justify-between mb-4">
                  <h3 className="text-base font-semibold text-primary">{t('scooters.filtersTitle')}</h3>
                  {activeFilterCount > 0 && (
                    <button onClick={resetFilters} className="text-xs text-accent hover:underline">
                      {t('scooters.clearFilters', { count: activeFilterCount })}
                    </button>
                  )}
                </div>
                <div className="overflow-y-auto max-h-[calc(100vh-11rem)] px-5 pb-5">
                  <FilterPanel filters={filters} setFilters={setFilters} onReset={resetFilters} count={filtered.length} zones={zones} />
                </div>
              </div>
            </aside>

            {/* Results grid */}
            <main className="lg:col-span-3">
              {loadingListings ? (
                <div className="text-center py-16 card-dashboard" data-testid="scooters-loading">
                  <div className="inline-block w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin mb-3" />
                  <p className="text-sm text-ink-light">{t('scooters.loadingAvailable')}</p>
                </div>
              ) : filtered.length === 0 ? (
                <div className="text-center py-16 card-dashboard" data-testid="no-scooters">
                  <Bike className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <h3 className="text-lg font-semibold text-primary mb-2">
                    {t('scooters.noResultsTitle')}
                  </h3>
                  <p className="text-sm text-ink-light mb-4">
                    {t('scooters.noResultsBody')}
                  </p>
                  <button
                    onClick={() => setEditSearchOpen(true)}
                    className="btn-primary"
                    data-testid="reset-filters-empty"
                  >
                    {t('scooters.editSearch')}
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="scooters-grid">
                  {filtered.map((s) => <ScooterCard key={s.id} scooter={s} isFavorite={favIds.has(s.id)} onToggleFavorite={toggleFavorite} periodQuery={periodQuery} />)}
                </div>
              )}
            </main>
          </div>
        </div>
      </div>
    </div>
  );
};
